import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, ShieldCheck, Headphones } from 'lucide-react';

export default function Home() {
  const [products, setProducts] = useState([]);
  const [wishlist, setWishlist] = useState(() => {
    try {
      const savedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      return Array.isArray(savedWishlist) ? savedWishlist : [];
    } catch (err) {
      return [];
    }
  });

  const toggleWishlist = (productId) => {
    setWishlist((currentWishlist) => {
      const nextWishlist = currentWishlist.includes(productId)
        ? currentWishlist.filter((id) => id !== productId)
        : [...currentWishlist, productId];

      localStorage.setItem('wishlist', JSON.stringify(nextWishlist));
      window.dispatchEvent(new Event('wishlistUpdated'));
      return nextWishlist;
    });
  };

  useEffect(() => {
    // Fetch live inventory items added from the admin dashboard
    const fetchProducts = async () => {
      try {
        let response = await fetch('http://localhost:5000/api/products');

        if (!response.ok) {
          response = await fetch('/api/products');
        }

        if (!response.ok) {
          throw new Error(`Products request failed with status ${response.status}`);
        }

        const responseData = await response.json();
        const data = Array.isArray(responseData) ? responseData : responseData.products || [];
        setProducts(data.slice(0, 4));
      } catch (err) {
        console.error("Error fetching products:", err);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div 
      className="text-[#1A1A1A] font-sans antialiased min-h-screen bg-cover bg-repeat" 
      style={{ backgroundImage: "url('/bg-parchment.jpg')" }}
    >
      
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-1 sm:pt-2 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 space-y-8">
          <div className="inline-block uppercase tracking-[0.2em] text-xs font-semibold text-[#A39171] border-b border-[#A39171]/40 pb-1">
            Wholesale & Own Manufacturing
          </div>
          <h1 className="font-serif text-[#1A1A1A] text-5xl sm:text-6xl lg:text-7xl font-normal tracking-tight uppercase leading-[1.05]">
            Handloom<br />Sarees
          </h1>
          <p className="text-[#1A1A1A]/70 text-base sm:text-lg font-light leading-relaxed max-w-lg">
            Wholesale sarees and own manufacturings. Big Savings on bulk sarees with peerless traditional craftsmanship.
          </p>
          <div className="flex items-center gap-4 pt-2">
            <Link to="/explore" className="inline-flex items-center justify-center px-8 py-4 border border-[#1A1A1A] text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#1A1A1A] hover:text-[#F9F8F6] transition-all duration-300">
              Explore Sarees
            </Link>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="aspect-[4/5] overflow-hidden rounded-sm bg-stone-200/20 shadow-xl">
            <img src="/hero-lady.jpg" alt="Model in sophisticated neutral-toned handloom saree" className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-700" />
          </div>
        </div>
      </section>

      {/* Product Grid ("From the boutique" - Exactly 1 Row of 4 Sarees from Admin Dashboard) */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row justify-between items-baseline mb-12 border-b border-[#1A1A1A]/10 pb-6">
          <div>
            <h2 className="font-serif text-3xl font-normal">From the boutique</h2>
            <p className="text-xs tracking-widest uppercase text-[#1A1A1A]/60 mt-1">Pieces tailored for you from our inventory.</p>
          </div>
          <Link to="/explore" className="text-xs tracking-widest uppercase underline underline-offset-4 mt-4 md:mt-0 font-medium hover:text-[#A39171] transition-colors">View All Sarees</Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {products.length > 0 ? (
            products.map((product) => {
              const productId = product._id || product.id;

              return (
              <div key={product._id || product.id} className="group flex flex-col">
                <div className="relative aspect-[3/4] bg-white/40 overflow-hidden mb-4 border border-[#1A1A1A]/5 shadow-sm">
                  <img 
                    src={product.image || product.imageUrl || product.images?.[0] || "/hero-lady.jpg"} 
                    alt={product.name || product.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                  <button
                    type="button"
                    aria-label={wishlist.includes(productId) ? "Remove from Wishlist" : "Add to Wishlist"}
                    aria-pressed={wishlist.includes(productId)}
                    onClick={() => toggleWishlist(productId)}
                    className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full text-[#1A1A1A] hover:text-rose-600 transition-colors"
                  >
                    <Heart className="w-4 h-4" fill={wishlist.includes(productId) ? "currentColor" : "none"} />
                  </button>
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex justify-center">
                    <Link to={`/product/${product._id || product.id}`} className="w-full bg-white text-[#1A1A1A] text-xs tracking-widest uppercase font-semibold py-2.5 text-center shadow-md hover:bg-[#1A1A1A] hover:text-white transition-colors">
                      View / Add
                    </Link>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] tracking-widest uppercase text-[#A39171] font-medium">{product.category || "Handloom Saree"}</p>
                  <h3 className="font-serif text-base font-normal truncate">{product.name || product.title}</h3>
                  <p className="text-xs font-semibold text-[#1A1A1A]">₹{product.price}</p>
                </div>
              </div>
              );
            })
          ) : (
            <p className="text-xs text-charcoal/60 col-span-4 text-center py-8">Loading inventory items from dashboard...</p>
          )}
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="border-y border-[#1A1A1A]/10 py-12 bg-white/20 backdrop-blur-[2px] my-12">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="flex flex-col items-center space-y-3">
            <div className="p-3 bg-white/60 rounded-full text-[#A39171] shadow-sm">
              <Sparkles className="w-6 h-6" />
            </div>
            <h3 className="font-serif text-lg font-medium">Authentic Sarees</h3>
            <p className="text-xs text-[#1A1A1A]/60 max-w-xs">Handcrafted directly from authentic regional looms with traditional motifs.</p>
          </div>
          <div className="flex flex-col items-center space-y-3">
            <div className="p-3 bg-white/60 rounded-full text-[#A39171] shadow-sm">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="font-serif text-lg font-medium">Quality Assured</h3>
            <p className="text-xs text-[#1A1A1A]/60 max-w-xs">Rigorous multi-stage checks ensuring absolute fabric and zari durability.</p>
          </div>
          <div className="flex flex-col items-center space-y-3">
            <div className="p-3 bg-white/60 rounded-full text-[#A39171] shadow-sm">
              <Headphones className="w-6 h-6" />
            </div>
            <h3 className="font-serif text-lg font-medium">Easy Support</h3>
            <p className="text-xs text-[#1A1A1A]/60 max-w-xs">Dedicated wholesale and retail client assistance available round the clock.</p>
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section id="about" className="py-16 my-8">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6 bg-white/30 backdrop-blur-sm py-16 px-8 border border-[#1A1A1A]/5 rounded-sm shadow-sm">
          <span className="text-xs tracking-[0.2em] uppercase text-[#A39171] font-semibold">About Collector</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-normal leading-snug">A celebration of timeless Indian elegance.</h2>
          <div className="w-12 h-[1px] bg-[#A39171] mx-auto"></div>
          <p className="text-[#1A1A1A]/70 text-sm sm:text-base font-light leading-relaxed max-w-2xl mx-auto">
            Sri Dorasani Collections is where you find pure handloom sarees from our own manufactures. We bridge traditional artisan communities directly with boutique owners and connoisseurs, guaranteeing exceptional quality and unmatched wholesale value.
          </p>
          <div className="pt-4">
            <img src="/logo.png" alt="Sri Dorasani Collections Signature" className="h-12 w-auto mx-auto opacity-80" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-[#F9F8F6] pt-16 pb-12 mt-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 border-b border-white/10 pb-12">
          <div className="space-y-4 md:col-span-1">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Sri Dorasani Collections Logo" className="h-8 w-auto brightness-200" />
              <span className="font-serif text-lg tracking-wider font-semibold text-white">Sri Dorasani</span>
            </div>
            <p className="text-xs text-[#F9F8F6]/60 font-light leading-relaxed">
              Premium handloom sarees and dedicated wholesale manufacturing based on rich cultural heritage.
            </p>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37]">Shop</h4>
            <ul className="space-y-2 text-xs text-[#F9F8F6]/70 font-light">
              <li><Link to="/explore" className="hover:text-white transition-colors">Kanjeevaram Sarees</Link></li>
              <li><Link to="/explore" className="hover:text-white transition-colors">Banarasi Silks</Link></li>
              <li><Link to="/explore" className="hover:text-white transition-colors">Wholesale Catalog</Link></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37]">Customer Care</h4>
            <ul className="space-y-2 text-xs text-[#F9F8F6]/70 font-light">
              <li><Link to="/explore" className="hover:text-white transition-colors">Bulk Order Inquiry</Link></li>
              <li><Link to="/explore" className="hover:text-white transition-colors">Shipping & Delivery</Link></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37]">Contact</h4>
            <p className="text-xs text-[#F9F8F6]/70 font-light leading-relaxed">
              For wholesale inquiries and customer support, reach out to us directly at:
            </p>
            <a href="mailto:sridorasanicollections@gmail.com" className="text-xs text-white underline underline-offset-4 block hover:text-[#D4AF37] transition-colors">
              sridorasanicollections@gmail.com
            </a>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-8 flex flex-col sm:flex-row justify-between items-center text-[11px] text-[#F9F8F6]/50">
          <p>© 2026 Sri Dorasani Collections by Vasantha. ALL RIGHTS RESERVED.</p>
        </div>
      </footer>
    </div>
  );
}